import { Circle, InfoOutlined, RadioButtonUnchecked, Straighten } from '@mui/icons-material';
import { Box, ClickAwayListener, Modal, Radio, Switch, Tooltip } from '@mui/material';
import React from 'react';
import { useIntl } from 'react-intl';
import { EUnitType } from '../../../../@types/Enums';
import { IconMagnet } from '../../../../assets/icons';
import { useMultiSelectToggleContext } from '../../../../provider/MultiSelectToggleProvider';
import { useToolbarContext } from '../../../../provider/ToolbarProvider';
import { useUnitContext } from '../../../../provider/UnitProvider';
import { immersightBlue, immersightBlueDarker3, immersightGrey25, primaryIconInactive, secondaryIconHover } from '../../../../utils/colors';
import ImsIcon from '../../ImsIcon';

interface BehaviorModalProps {
  open: boolean;
  onClose: () => void;
  sizes: any;
  buttonPosition?: DOMRect;
}

const BehaviorModal: React.FC<BehaviorModalProps> = ({ open, onClose, sizes, buttonPosition }) => {
  const intl = useIntl();
  const { isSnappingEnabled, setIsSnappingEnabled } = useMultiSelectToggleContext();
  const { setCurrentlyActiveSubMenu } = useToolbarContext();
  const { unitType, setUnitType } = useUnitContext();

  const handleItemClicked = (id: number) => {
    switch (id) {
      case 0:
        setIsSnappingEnabled(!isSnappingEnabled);
        break;
      case 1:
        setUnitType(unitType === EUnitType.IMPERIAL ? EUnitType.METRIC : EUnitType.IMPERIAL);
        break;
      default:
        break;
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      sx={{
        zIndex: 1002,
        position: 'fixed',
        left: buttonPosition ? `${buttonPosition.right + 10}px` : '2.5rem',
        top: buttonPosition ? `${buttonPosition.top - 48}px` : '50%',
        width: 'fit-content',
        transform: 'none',
        overflow: 'visible',
        '& .MuiBackdrop-root': { display: 'none' },
      }}
    >
      <Box>
        <ClickAwayListener onClickAway={onClose}>
          <Box
            sx={{
              position: 'relative',
              borderRadius: '1.5rem',
              boxShadow: '0px 2px 4px -1px rgba(0,0,0,0.2), 0px 4px 5px 0px rgba(0,0,0,0.14), 0px 1px 10px 0px rgba(0,0,0,0.12)',
              bgcolor: 'white',
              padding: '0.5rem 0',
              margin: '0',
              transition: 'opacity 225ms cubic-bezier(0.4, 0, 0.2, 1) 0ms',
            }}
          >
            {/* Title Section with Dividing Line */}
            <Box
              sx={{
                padding: '0.5rem 1rem',
                borderBottom: '1px solid rgba(0, 0, 0, 0.12)',
                marginBottom: '0.5rem',
              }}
            >
              <Box
                sx={{
                  fontSize: '1.1rem',
                  fontWeight: 500,
                  color: 'rgba(0, 0, 0, 0.87)',
                }}
              >
                {intl.formatMessage({ id: 'Toolbar.behavior' })}
              </Box>
            </Box>
            {/* Behavior Items */}
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
              }}
            >
              {/* First Behavior Item */}
              <Box
                component="div"
                onClick={() => handleItemClicked(0)}
                sx={{
                  display: 'grid',
                  gridTemplateRows: '1fr',
                  gridTemplateColumns: '18% 60% 5% 8% 9%',
                  alignItems: 'center',
                  padding: '0.1rem 0.7rem',
                  fontSize: '1rem',
                  cursor: 'pointer',
                  borderRadius: 0,
                  color: isSnappingEnabled ? immersightBlue : primaryIconInactive,
                  width: 'min(95vw, 20rem)',
                  userSelect: 'none',
                  '&:hover': {
                    bgcolor: secondaryIconHover,
                  },
                }}
              >
                <ImsIcon Icon={IconMagnet} style={{ width: '2.25rem', height: '2.25rem', margin: '.1rem .6rem', transition: '.2s ease-in-out' }} />
                {intl.formatMessage({ id: 'SnappingToggle.snapping' })}
                <Radio
                  checked={isSnappingEnabled}
                  icon={<RadioButtonUnchecked />}
                  checkedIcon={<Circle />}
                  sx={{
                    color: primaryIconInactive,
                    '&.Mui-checked': {
                      color: immersightBlue,
                      strokeWidth: 2,
                    },
                  }}
                />
                <Tooltip title={intl.formatMessage({ id: 'SnappingToggle.description' })} placement="top">
                  <Box
                    component="div"
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      color: primaryIconInactive,
                      marginLeft: '0.3rem',
                      cursor: 'default',
                    }}
                  >
                    <InfoOutlined />
                  </Box>
                </Tooltip>
              </Box>
              {/* Second Behavior Item */}
              <Box
                component="div"
                onClick={() => handleItemClicked(1)}
                sx={{
                  display: 'grid',
                  gridTemplateRows: '1fr',
                  gridTemplateColumns: '18% 50.5% 14.5% 8% 9%',
                  alignItems: 'center',
                  padding: '0.1rem 0.7rem',
                  fontSize: '1rem',
                  cursor: 'pointer',
                  borderRadius: 0,
                  color: unitType === EUnitType.METRIC ? immersightBlue : immersightBlueDarker3,
                  width: 'min(95vw, 20rem)',
                  userSelect: 'none',
                  '&:hover': {
                    bgcolor: secondaryIconHover,
                  },
                }}
              >
                <Straighten
                  sx={{
                    width: '2.25rem',
                    height: '2.25rem',
                    margin: '.1rem .5rem',
                    transition: '.2s ease-in-out',
                  }}
                />
                {unitType === EUnitType.METRIC ? intl.formatMessage({ id: 'BehaviorDropdown.metric' }) : intl.formatMessage({ id: 'BehaviorDropdown.imperial' })}
                <Switch
                  checked={unitType === EUnitType.METRIC}
                  sx={{
                    scrollbarColor: immersightBlue,
                    '& .MuiSwitch-thumb': {
                      color: unitType === EUnitType.METRIC ? immersightBlue : immersightBlueDarker3,
                    },
                    '& .MuiSwitch-track': {
                      backgroundColor: unitType === EUnitType.METRIC ? `${immersightGrey25} !important` : primaryIconInactive,
                    },
                    marginRight: '1.5rem',
                  }}
                />
                <Tooltip title={intl.formatMessage({ id: 'BehaviorDropdown.unitInfo' })} placement="bottom">
                  <Box
                    component="div"
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      color: primaryIconInactive,
                      marginLeft: '0.3rem',
                      cursor: 'default',
                    }}
                  >
                    <InfoOutlined />
                  </Box>
                </Tooltip>
              </Box>
            </Box>
          </Box>
        </ClickAwayListener>
      </Box>
    </Modal>
  );
};

export default BehaviorModal;
